import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getCheckoutPageUrl, recordPayFunnelEvent, startProTrial } from '@extension/wa-communication-layer/api/api-calls';
import { openPaymentSite } from '@common-services/utils';
import { useReviewAskBlocker } from '@common/components/nudge-banner/review-ask-blockers';
import { appConfig } from '@common-config/app.config';
import { getWallExposure, setOfferDeclined, shouldFireSoftShown, WallState } from '@common/paywall/wall-exposure';
import { DBSubscriptionThunks } from '@common-reducers/DBServiceThunks';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { PAYWALL_VARIANTS, PaywallFeature, itemPriceId, SOFT_SCHEDULING_OFFER, SCHEDULING_HINT } from './paywall-variants';
import { isClearFlow } from '@common/utils/clear-flow';

type Cycle = 'Yearly' | 'Monthly';
export type { WallState };


/** Opens a blank tab synchronously (inside user activation, so it survives the popup
 * blocker) and immediately paints a branded loading state into it, so the user isn't
 * staring at an empty about:blank tab for the several seconds it takes to mint the
 * Chargebee checkout URL. The subsequent `tab.location.href = url` navigation (done by
 * the caller) simply replaces this markup — no change to the existing navigation logic. */
function openCheckoutLoadingTab(): Window | null {
  const tab = window.open('about:blank', '_blank');
  if (tab) {
    tab.opener = null;
    try {
      tab.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Preparing checkout…</title>
      <style>
        html,body{height:100%;margin:0}
        body{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:22px;
             font-family:-apple-system,Segoe UI,Roboto,sans-serif;background:#0C2D3F;color:#fff}
        .sp{width:44px;height:44px;border:4px solid rgba(255,255,255,.25);border-top-color:#259AE9;border-radius:50%;animation:s 0.9s linear infinite}
        @keyframes s{to{transform:rotate(360deg)}}
        .t{font-size:18px;font-weight:600} .s{font-size:14px;opacity:.7}
      </style></head><body>
        <div class="sp"></div>
        <div class="t">Preparing your secure checkout…</div>
        <div class="s">One moment — taking you to Blueticks checkout.</div>
      </body></html>`);
      tab.document.close();
    } catch { /* cross-origin/blocked write — ignore, blank tab still navigates */ }
  }
  return tab;
}

interface FeatureAwarePaywallProps {
  feature: PaywallFeature;
  onClose: () => void;
  /** Option-3 (past-fatigue-cap) treatment. Defaults to 'hard' so every existing
   * caller keeps today's unchanged behaviour. */
  wallState?: WallState;
  /** #137 A1: the plan-by-engine arm, stamped on the checkout events. Absent when the experiment is off. */
  planArm?: string;
}

/** Feature-aware paywall body (#131). Rendered inside the caller's DialogContent.
 * Annual-anchored billing toggle; CTA deep-links to Chargebee hosted checkout
 * (chargebee.getCheckoutPage); falls back to the account-subscription page on error. */
export const FeatureAwarePaywall: React.FC<FeatureAwarePaywallProps> = ({ feature, onClose, wallState = 'hard', planArm }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  useReviewAskBlocker('paywall'); // #787: no review ask while a paywall is up
  const v = PAYWALL_VARIANTS[feature];
  const [cycle, setCycle] = useState<Cycle>('Yearly');
  const [busy, setBusy] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);

  const tv = (field: string, fallback: string) => t(`${v.i18nPrefix}.${field}`, fallback);
  const perMonth = cycle === 'Yearly' ? v.yearlyPerMonth : v.monthly;

  const eventProps = (extra?: Record<string, unknown>) => ({
    feature: v.feature, plan: v.plan, cycle, item_price_id: itemPriceId(v.plan, cycle),
    variant: 'feature_modal', ...(planArm && planArm !== 'off' ? { plan_arm: planArm } : {}), ...extra,
  });

  // Fire scheduling_wall_soft_shown ONCE per show for the soft (offer/hint) states —
  // guarded by a ref (component-instance-scoped, mirroring the hard path's redux-based
  // dedup one level up in the popup) so re-renders of the same show never double-fire.
  // The ref alone isn't enough: a rapid open/close remount loop (the same failure mode
  // the wall_hit event guards against) resets the ref on every remount and would re-fire
  // the event many times. shouldFireSoftShown is a module-level throttle (own clock, NOT
  // shared with shouldFireWallHit — different event) that survives remounts.
  const firedSoftRef = useRef<string | null>(null);
  useEffect(() => {
    if (wallState === 'hard') return;
    if (firedSoftRef.current === wallState) return;
    firedSoftRef.current = wallState;
    if (!shouldFireSoftShown(Date.now())) return;
    recordPayFunnelEvent('scheduling_wall_soft_shown', { state: wallState, exposures: getWallExposure() }).catch(() => {});
  }, [wallState]);

  const startOfferCheckout = async () => {
    if (offerBusy) return;
    setOfferBusy(true);
    recordPayFunnelEvent('wall_modal_checkout_started', { feature: 'scheduling', variant: 'soft_offer', action: 'start_pro_trial' }).catch(() => {});
    // Same synchronous-tab-open trick as the hard CTA below: open the tab inside user
    // activation so a fallback-checkout navigation (after the async calls) isn't blocked
    // by Chrome's popup blocker. Closed unused if the trial itself starts successfully —
    // no navigation needed in that case.
    const tab = openCheckoutLoadingTab();
    const accountSubscriptionUrl = `${appConfig.PRICING_PAGE}/account-subscription?v=1&accessToken=${localStorage.getItem('bt-token') ?? ''}`;

    // Ineligible (workspace already had a subscription at some point), an error, or any
    // other non-ok response — fall back to the existing Pro checkout, same as the hard CTA.
    const fallbackToProCheckout = async () => {
      try {
        const res = await getCheckoutPageUrl('Pro-USD-Monthly');
        if (res?.checkoutPageUrl) {
          if (tab) {
            tab.location.href = res.checkoutPageUrl;
          } else {
            window.open(res.checkoutPageUrl, '_blank');
          }
        } else if (tab) {
          tab.location.href = accountSubscriptionUrl;
        } else {
          await openPaymentSite(localStorage.getItem('bt-token') ?? '', true);
        }
      } catch {
        if (tab) {
          tab.location.href = accountSubscriptionUrl;
        } else {
          await openPaymentSite(localStorage.getItem('bt-token') ?? '', true);
        }
      }
    };

    try {
      const res = await startProTrial();
      if (res?.ok) {
        tab?.close();
        toast.success(t('paywall.trialStarted', 'Your 7-day Pro trial is active'));
        // Belt: mark the offer consumed so that IF the subscription refresh below
        // hasn't propagated yet (Chargebee list cache / round-trip lag) and the user
        // reopens still over-limit, they drop to the quiet 'hint' — never re-shown
        // the offer, which would return `ineligible` and bounce them to a PAID
        // checkout right after they accepted a free trial.
        setOfferDeclined();
        // Refresh the subscription list so messageLimitSelector (and therefore
        // isOverLimit) re-evaluates against the trial that just started. The backend
        // action invalidates its subscription-list cache before returning, so this
        // refetch reads the fresh list that now includes the trial.
        dispatch(DBSubscriptionThunks.find({}));
        onClose();
      } else {
        // Ineligible (workspace already trialed/subscribed) or any other non-ok response —
        // treat it like a decline so this user drops to the quiet 'hint' next time instead
        // of re-looping into a Pro-checkout tab on every over-limit open.
        if (res?.ineligible) setOfferDeclined();
        await fallbackToProCheckout();
      }
    } catch {
      await fallbackToProCheckout();
    } finally {
      setOfferBusy(false);
    }
  };

  const declineOffer = () => {
    setOfferDeclined();
    recordPayFunnelEvent('wall_modal_dismissed', { feature: 'scheduling', variant: 'soft_offer' }).catch(() => {});
    onClose();
  };

  const selectCycle = (c: Cycle) => {
    setCycle(c);
    recordPayFunnelEvent('wall_modal_plan_click', { ...eventProps(), cycle: c, item_price_id: itemPriceId(v.plan, c) }).catch(() => {});
  };

  const startCheckout = async () => {
    if (busy) return;
    setBusy(true);
    recordPayFunnelEvent('wall_modal_checkout_started', eventProps()).catch(() => {});
    // Open the tab synchronously, inside user activation, so the later navigation
    // (after the async URL mint) isn't blocked by Chrome's popup blocker.
    const tab = openCheckoutLoadingTab();
    const accountSubscriptionUrl = `${appConfig.PRICING_PAGE}/account-subscription?v=1&accessToken=${localStorage.getItem('bt-token') ?? ''}`;
    try {
      const res = await getCheckoutPageUrl(itemPriceId(v.plan, cycle));
      if (res?.checkoutPageUrl) {
        if (tab) {
          tab.location.href = res.checkoutPageUrl;
        } else {
          // Popup was blocked — a direct open here may still prompt the user.
          window.open(res.checkoutPageUrl, '_blank');
        }
      } else if (tab) {
        // Fallback: the pre-modal path — account-subscription page, reusing the placeholder tab.
        tab.location.href = accountSubscriptionUrl;
      } else {
        await openPaymentSite(localStorage.getItem('bt-token') ?? '', true);
      }
    } catch {
      if (tab) {
        tab.location.href = accountSubscriptionUrl;
      } else {
        await openPaymentSite(localStorage.getItem('bt-token') ?? '', true);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCompareAll = () => {
    recordPayFunnelEvent('wall_modal_plan_click', eventProps({ target: 'compare_all' })).catch(() => {});
    openPaymentSite(localStorage.getItem('bt-token') ?? '', true).catch(() => {});
    onClose();
  };

  // The clear flow is white-labelled and has no subscription surface of its own.
  // Every route out of this wall is Blueticks-branded — the Chargebee hand-off
  // ("taking you to Blueticks checkout"), "Compare all plans" (openPaymentSite),
  // and the variant copy itself, which names Blueticks. So the flow states the
  // limit and offers no upgrade path, rather than selling the other brand at the
  // moment of conversion. The cap itself is unchanged: this only removes CTAs.
  if (isClearFlow()) {
    return (
      <div style={{ padding: 4, fontFamily: 'inherit' }}>
        <h3 style={{ fontSize: 19, lineHeight: 1.3, fontWeight: 600, margin: '4px 0 8px' }}>
          {t('scheduledMessage.limits.freeScheduleCap', 'Three scheduled messages at a time')}
        </h3>
        <p style={{ fontSize: 13.5, color: '#51677a', margin: '0 0 18px' }}>
          {t('paywall.clear.body', 'Send or delete one of your scheduled messages to free up a slot.')}
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            display: 'block', width: '100%', border: 0, borderRadius: 10, padding: '13px 16px',
            background: 'var(--cta-button-bg, #2B7FFF)', color: '#fff',
            fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {t('paywall.dismiss', 'Got it')}
        </button>
      </div>
    );
  }

  // Option-3: past the fatigue cap, a one-time free-month Pro offer...
  if (wallState === 'offer') {
    return (
      <div style={{ padding: 4, fontFamily: 'inherit' }}>
        <span style={{
          display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: '#0f7fbe', background: '#e7f4fb',
          borderRadius: 999, padding: '4px 10px',
        }}>
          {t('paywall.softOffer.eyebrow', SOFT_SCHEDULING_OFFER.eyebrow)}
        </span>
        <h3 style={{ fontSize: 21, lineHeight: 1.25, fontWeight: 600, margin: '14px 0 6px' }}>
          {t('paywall.softOffer.title', SOFT_SCHEDULING_OFFER.title)}
        </h3>
        <p style={{ fontSize: 13.5, color: '#51677a', margin: '0 0 18px' }}>{t('paywall.softOffer.sub', SOFT_SCHEDULING_OFFER.sub)}</p>

        <button
          type="button"
          onClick={startOfferCheckout}
          disabled={offerBusy}
          style={{
            display: 'block', width: '100%', border: 0, borderRadius: 10, padding: '13px 16px',
            background: 'linear-gradient(180deg, #22a7ef, #1691d6)', color: '#fff',
            fontSize: 15, fontWeight: 600, cursor: offerBusy ? 'wait' : 'pointer', fontFamily: 'inherit',
            opacity: offerBusy ? 0.7 : 1,
          }}
        >
          {t('paywall.softOffer.primaryCta', SOFT_SCHEDULING_OFFER.primaryCta)}
        </button>

        <button
          type="button"
          onClick={declineOffer}
          style={{
            display: 'block', width: '100%', border: 0, background: 'transparent',
            color: '#51677a', fontSize: 13, padding: '12px 0 0', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {t('paywall.softOffer.secondaryCta', SOFT_SCHEDULING_OFFER.secondaryCta)}
        </button>
      </div>
    );
  }

  // ...and, if declined, a quiet non-blocking hint (never the hard modal again).
  if (wallState === 'hint') {
    return (
      <div style={{ padding: 4, fontFamily: 'inherit' }}>
        <p style={{ fontSize: 12.5, color: '#7d92a3', margin: 0, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <span>{t('paywall.hint', SCHEDULING_HINT)}</span>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 0, background: 'transparent', color: '#0f7fbe', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
          >
            {t('paywall.dismiss', 'Got it')}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 4, fontFamily: 'inherit' }}>
      <span style={{
        display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: '#0f7fbe', background: '#e7f4fb',
        borderRadius: 999, padding: '4px 10px',
      }}>
        {tv('eyebrow', v.eyebrow)}
      </span>
      <h3 style={{ fontSize: 21, lineHeight: 1.25, fontWeight: 600, margin: '14px 0 6px' }}>
        {tv('title', v.title)}
      </h3>
      <p style={{ fontSize: 13.5, color: '#51677a', margin: '0 0 16px' }}>{tv('sub', v.sub)}</p>

      {v.keep && (
        <div style={{
          fontSize: 12, color: '#1c7f4b', background: '#e9f7ef', borderRadius: 8,
          padding: '7px 10px', marginBottom: 14,
        }}>
          ✓ {tv('keep', v.keep)}
        </div>
      )}

      <ul style={{ listStyle: 'none', margin: '0 0 18px', padding: 0, display: 'grid', gap: 9 }}>
        {v.bullets.map((b, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, fontSize: 13.5, alignItems: 'flex-start' }}>
            <span style={{ color: '#1d9be0', fontWeight: 700 }}>✓</span>
            {tv(`bullet${i + 1}`, b)}
          </li>
        ))}
      </ul>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        {(['Yearly', 'Monthly'] as Cycle[]).map((c) => {
          const selected = cycle === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => selectCycle(c)}
              style={{
                border: selected ? '1.5px solid #1d9be0' : '1.5px solid #d8e2ea',
                background: selected ? '#f2fafe' : '#fff',
                boxShadow: selected ? '0 0 0 3px rgba(29,155,224,.12)' : 'none',
                borderRadius: 10, padding: '10px 12px', cursor: 'pointer', textAlign: 'start',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: '#51677a', display: 'flex', justifyContent: 'space-between' }}>
                {c === 'Yearly' ? t('paywall.billing.yearly', 'Yearly') : t('paywall.billing.monthly', 'Monthly')}
                {c === 'Yearly' && (
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#1c7f4b', background: '#dcf4e6', padding: '2px 7px', borderRadius: 999 }}>
                    {t('paywall.billing.save', 'SAVE {{pct}}%', { pct: v.savePct })}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 17, fontWeight: 600, marginTop: 3, display: 'block' }}>
                ${c === 'Yearly' ? v.yearlyPerMonth : v.monthly}
                <small style={{ fontSize: 11.5, fontWeight: 500, color: '#7d92a3' }}>
                  {c === 'Yearly'
                    ? t('paywall.billing.perMonthYearly', '/mo · ${{total}} billed yearly', { total: v.yearlyTotal })
                    : t('paywall.billing.perMonth', '/mo')}
                </small>
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={startCheckout}
        disabled={busy}
        style={{
          display: 'block', width: '100%', border: 0, borderRadius: 10, padding: '13px 16px',
          background: 'linear-gradient(180deg, #22a7ef, #1691d6)', color: '#fff',
          fontSize: 15, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {tv('cta', v.cta)} · ${perMonth}{t('paywall.billing.perMonth', '/mo')}
      </button>

      <p style={{ textAlign: 'center', margin: '12px 0 0', fontSize: 12.5 }}>
        <a href="#" onClick={(e) => { e.preventDefault(); handleCompareAll(); }} style={{ color: '#0f7fbe', textDecoration: 'none' }}>
          {t('paywall.compareAll', 'Compare all plans')}
        </a>
      </p>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #edf2f6', fontSize: 11, color: '#8aa0b0', textAlign: 'center' }}>
        {t('paywall.trust', '14-day money-back · Cancel anytime · No per-message fees')}
      </div>
    </div>
  );
};

export default FeatureAwarePaywall;
